/**
 * HolidaysListPage
 * Main page for managing holidays
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Download,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { HolidayFormDialog, HolidayImportDialog } from './components';
import { holidayService } from '../../services/holidayService';
import {
  Holiday,
  HolidayFormData,
  HolidayType,
  HOLIDAY_TYPE_LABELS,
  getAvailableYears,
} from '../../types/holiday';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { useAcao } from '../../authorization/useVeredito';
import { ActionButton } from '../../components/common';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Format date for display
const formatDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), "dd 'de' MMMM", { locale: ptBR });
  } catch {
    return dateString;
  }
};

// Get type chip color
const getTypeChipColor = (type: HolidayType): 'primary' | 'secondary' | 'info' | 'warning' => {
  switch (type) {
    case HolidayType.NACIONAL:
      return 'primary';
    case HolidayType.ESTADUAL:
      return 'secondary';
    case HolidayType.MUNICIPAL:
      return 'info';
    case HolidayType.EMPRESA:
      return 'warning';
    default:
      return 'primary';
  }
};

const columns: DataTableColumn<Holiday>[] = [
  {
    id: 'date',
    label: 'Data',
    render: (holiday) => (
      <>
        <Typography variant="body2" fontWeight="medium">
          {formatDate(holiday.date)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {holiday.date}
        </Typography>
      </>
    ),
  },
  { id: 'name', label: 'Nome', primary: true, render: (holiday) => holiday.name },
  {
    id: 'type',
    label: 'Tipo',
    render: (holiday) => (
      <Chip
        label={HOLIDAY_TYPE_LABELS[holiday.type]}
        color={getTypeChipColor(holiday.type)}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    id: 'location',
    label: 'Local',
    render: (holiday) => (
      <>
        {holiday.type === HolidayType.ESTADUAL && holiday.state}
        {holiday.type === HolidayType.MUNICIPAL && `${holiday.city}, ${holiday.state}`}
        {holiday.type === HolidayType.EMPRESA && 'Empresa'}
        {holiday.type === HolidayType.NACIONAL && '-'}
      </>
    ),
  },
  {
    id: 'isRecurring',
    label: 'Recorrente',
    render: (holiday) => (
      <Chip
        label={holiday.isRecurring ? 'Sim' : 'Não'}
        color={holiday.isRecurring ? 'success' : 'default'}
        size="small"
      />
    ),
  },
];

const HolidaysListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { effectiveCompanyId } = useCompanyContext();
  const editar = useAcao('holiday.update', 'Editar');
  const excluir = useAcao('holiday.delete', 'Excluir');

  // Data states
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  // Filters
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [typeFilter, setTypeFilter] = useState<HolidayType | ''>('');

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);

  const years = getAvailableYears();

  // Load holidays
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await holidayService.getHolidays({
        year: yearFilter,
        type: typeFilter || undefined,
        companyId: effectiveCompanyId ?? undefined,
        page: page + 1,
        limit: rowsPerPage,
      });

      setHolidays(response.holidays);
      setTotal(response.total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar feriados';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [yearFilter, typeFilter, effectiveCompanyId, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle new holiday
  const handleNewHoliday = () => {
    setEditingHoliday(null);
    setFormDialogOpen(true);
  };

  // Handle edit holiday
  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormDialogOpen(true);
  };

  // Handle form submit
  const handleFormSubmit = async (data: HolidayFormData) => {
    if (editingHoliday) {
      await holidayService.updateHoliday(editingHoliday.id, data);
      enqueueSnackbar('Feriado atualizado com sucesso!', { variant: 'success' });
    } else {
      await holidayService.createHoliday(data);
      enqueueSnackbar('Feriado criado com sucesso!', { variant: 'success' });
    }
    setFormDialogOpen(false);
    setEditingHoliday(null);
    loadData();
  };

  // Handle delete holiday
  const handleDeleteHoliday = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este feriado?')) {
      return;
    }

    try {
      await holidayService.deleteHoliday(id);
      enqueueSnackbar('Feriado excluído com sucesso!', { variant: 'success' });
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir feriado';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Handle import
  const handleImport = async (year: number) => {
    const result = await holidayService.importNationalHolidays(year);
    loadData();
    return result;
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Feriados' },
      ]}
      title="Feriados"
      actions={
        <Tooltip title="Atualizar lista">
          <span>
            <IconButton onClick={loadData} disabled={loading}>
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Ano</InputLabel>
              <Select
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value as number);
                  setPage(0);
                }}
                label="Ano"
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value as HolidayType | '');
                  setPage(0);
                }}
                label="Tipo"
              >
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(HOLIDAY_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flexGrow: 1 }} />

            <ActionButton
              acao="holiday.create"
              rotulo="Importar Feriados"
              variant="outlined"
              startIcon={<Download />}
              onClick={() => setImportDialogOpen(true)}
            >
              Importar Feriados
            </ActionButton>

            <ActionButton
              acao="holiday.create"
              rotulo="Novo Feriado"
              variant="contained"
              startIcon={<Add />}
              onClick={handleNewHoliday}
            >
              Novo Feriado
            </ActionButton>
          </Box>
        </CardContent>
      </Card>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        rows={holidays}
        getRowId={(holiday) => holiday.id}
        loading={loading}
        emptyState={{
          title: `Nenhum feriado encontrado para ${yearFilter}`,
          description: 'Os feriados nacionais, estaduais e municipais já vêm com o sistema; os da empresa saem do cadastro feito no seu acesso.',
        }}
        actions={[
          {
            icon: <Edit fontSize="small" />,
            onClick: handleEditHoliday,
            ...editar,
          },
          {
            icon: <Delete fontSize="small" />,
            color: 'error',
            onClick: (holiday) => handleDeleteHoliday(holiday.id),
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      {/* Form Dialog */}
      <HolidayFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingHoliday(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={editingHoliday}
        companyId={effectiveCompanyId ?? undefined}
      />

      {/* Import Dialog */}
      <HolidayImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
      />
    </PageShell>
  );
};

export default HolidaysListPage;
